import React, { useState, useRef } from 'react'
import { Upload, message } from 'antd'
import type { UploadFile, UploadProps } from 'antd/lib/upload/interface'
import { useSensorTracking } from '../hooks/useSensorTracking'

interface XCPhotoUploadButtonProps {
  onPhotoSelect?: () => void
  onLocalUpload?: (file: File) => void
  beforeUpload?: () => boolean
  width?: number
  height?: number
}

const PhotoUploadButton: React.FC<XCPhotoUploadButtonProps> = ({
  onPhotoSelect,
  onLocalUpload,
  beforeUpload,
  width = 200,
  height = 200
}) => {
  const [isHovered, setIsHovered] = useState(false)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const { reportAISecurityPageActionClick } = useSensorTracking()

  const handleLocalUpload = () => {
    // 如果有beforeUpload函数，先进行验证
    if (beforeUpload) {
      const canUpload = beforeUpload()
      if (!canUpload) {
        // 如果返回false，不调起图片选择器
        return
      }
    }

    // 埋点：点击本地上传照片
    reportAISecurityPageActionClick({
      actionType: 'uploadPhotoIdentify'
    })
    if (fileInputRef.current) {
      fileInputRef.current.click()
    }
  }

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (file) {
      // 验证文件类型
      const isImage = file.type.startsWith('image/')
      if (!isImage) {
        message.error('请选择图片文件！')
        return
      }

      // 验证文件大小（限制为10MB）
      const isLt10M = file.size / 1024 / 1024 < 10
      if (!isLt10M) {
        message.error('图片大小不能超过10MB！')
        return
      }

      onLocalUpload && onLocalUpload(file)
    }
    // 清空input值，允许重复选择同一文件
    event.target.value = ''
  }

  const defaultStyle = {
    width,
    height,
    border: '2px dashed #1890ff',
    borderRadius: '4px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    backgroundColor: '#f0f8ff',
    transition: 'none'
    // position: 'relative' as const
  }

  const buttonStyle = {
    width: '80%',
    height: '44px', // 减少按钮高度，减少内边距
    border: '1px solid #1890ff',
    borderRadius: '4px',
    backgroundColor: '#ffffff',
    color: '#1890ff',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '12px',
    margin: '6px 0',
    padding: '0 8px'
  }

  const buttonHoverStyle = {
    backgroundColor: '#1890ff',
    color: '#ffffff',
    border: '1px solid #1890ff',
    fontSize: '14px', // hover时字号加大
    fontWeight: 'bold' // hover时字体加粗
  }

  return (
    <div
      style={defaultStyle}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        style={{ display: 'none' }}
        onChange={handleFileChange}
      />

      {!isHovered ? (
        // 默认样式
        <div
          style={{
            textAlign: 'center',
            color: '#1890ff'
          }}
        >
          <div
            style={{
              fontSize: '24px',
              fontWeight: 'bold'
            }}
          >
            +
          </div>
          <div
            style={{
              fontSize: '14px',
              fontWeight: 'bold',
              whiteSpace: 'pre-line'
            }}
          >{`上传照片\n开始识别`}</div>
        </div>
      ) : (
        // 悬停样式 - 显示两个按钮
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            width: '100%',
            height: '100%'
          }}
        >
          <div
            style={buttonStyle}
            onClick={(e) => {
              e.stopPropagation()
              handleLocalUpload()
            }}
            onMouseEnter={(e) => {
              Object.assign(e.currentTarget.style, buttonHoverStyle)
            }}
            onMouseLeave={(e) => {
              Object.assign(e.currentTarget.style, {
                backgroundColor: '#ffffff',
                color: '#1890ff',
                border: '1px solid #1890ff'
              })
            }}
          >
            本地上传
          </div>
          <div
            style={buttonStyle}
            onClick={(e) => {
              e.stopPropagation()
              // 埋点：点击相册选图
              reportAISecurityPageActionClick({
                actionType: 'choosePhotoIdentify'
              })
              onPhotoSelect && onPhotoSelect()
            }}
            onMouseEnter={(e) => {
              Object.assign(e.currentTarget.style, buttonHoverStyle)
            }}
            onMouseLeave={(e) => {
              Object.assign(e.currentTarget.style, {
                backgroundColor: '#ffffff',
                color: '#1890ff'
              })
            }}
          >
            相册选图
          </div>
        </div>
      )}
    </div>
  )
}

export default PhotoUploadButton
